import type { Point, RGBA } from '../core/types';

/** Straight-alpha RGBA8 source image; never mutated by anything in this module. */
export interface ColorSelectImage {
  readonly pixels: Uint8Array;
  readonly width: number;
  readonly height: number;
}

function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** Integer 0..255 tolerance, matching floodFill's `Math.round(tolerance * 255)` convention. */
function toLimit(fraction: number): number {
  return Math.round(clamp01(fraction) * 255);
}

/**
 * Shared distance metric: the largest per-channel difference, alpha included so
 * transparent areas never match opaque ones. A per-channel max is what Photoshop's
 * wand behaves like in practice, and it is far cheaper than a real colour-space
 * distance (no conversions, no squares).
 */
function maxChannelDiff(
  pixels: Uint8Array,
  offset: number,
  r: number,
  g: number,
  b: number,
  a: number,
): number {
  return Math.max(
    Math.abs(pixels[offset] - r),
    Math.abs(pixels[offset + 1] - g),
    Math.abs(pixels[offset + 2] - b),
    Math.abs(pixels[offset + 3] - a),
  );
}

function pixelDiff(pixels: Uint8Array, offsetA: number, offsetB: number): number {
  return maxChannelDiff(
    pixels,
    offsetA,
    pixels[offsetB],
    pixels[offsetB + 1],
    pixels[offsetB + 2],
    pixels[offsetB + 3],
  );
}

/**
 * Hard-edged unless antialiasing is requested. When it is, the outer 25% of the
 * tolerance band ramps linearly from full to zero coverage instead of cutting off
 * sharply — a cheap stand-in for a real antialiased wand edge.
 */
function wandAlpha(diff: number, limit: number, antialias: boolean): number {
  if (diff > limit) return 0;
  if (!antialias) return 255;
  const softStart = limit * 0.75;
  if (diff <= softStart || limit <= 0) return 255;
  return Math.round(255 * (1 - (diff - softStart) / (limit - softStart)));
}

/** Photoshop's Magic Wand. */
export function magicWandCoverage(
  image: ColorSelectImage,
  seed: Point,
  options: { tolerance: number; contiguous: boolean; antialias?: boolean },
): Uint8Array {
  const { pixels, width, height } = image;
  const size = width * height;
  if (width <= 0 || height <= 0) return new Uint8Array(0);

  const result = new Uint8Array(size);
  const x0 = Math.floor(seed.x);
  const y0 = Math.floor(seed.y);
  if (x0 < 0 || y0 < 0 || x0 >= width || y0 >= height) return result;

  const limit = toLimit(options.tolerance);
  const antialias = options.antialias ?? false;
  const seedOffset = (y0 * width + x0) * 4;
  const sr = pixels[seedOffset];
  const sg = pixels[seedOffset + 1];
  const sb = pixels[seedOffset + 2];
  const sa = pixels[seedOffset + 3];
  const diffAt = (offset: number): number => maxChannelDiff(pixels, offset, sr, sg, sb, sa);

  if (!options.contiguous) {
    for (let i = 0; i < size; i += 1) {
      result[i] = wandAlpha(diffAt(i * 4), limit, antialias);
    }
    return result;
  }

  if (diffAt(seedOffset) > limit) return result;

  // Scanline flood, same shape as engine/floodFill.ts: an explicit stack of row
  // spans rather than per-pixel recursion, so it stays cheap on large documents.
  const visited = new Uint8Array(size);
  const matchesIndex = (index: number): boolean => diffAt(index * 4) <= limit;
  const stack: number[] = [x0, y0];

  while (stack.length > 0) {
    const y = stack.pop()!;
    const x = stack.pop()!;
    const row = y * width;
    if (visited[row + x]) continue;

    let left = x;
    while (left > 0 && !visited[row + left - 1] && matchesIndex(row + left - 1)) left -= 1;
    let right = x;
    while (right < width - 1 && !visited[row + right + 1] && matchesIndex(row + right + 1)) {
      right += 1;
    }

    for (let i = left; i <= right; i += 1) {
      visited[row + i] = 1;
      result[row + i] = wandAlpha(diffAt((row + i) * 4), limit, antialias);
    }

    for (const ny of [y - 1, y + 1]) {
      if (ny < 0 || ny >= height) continue;
      const nRow = ny * width;
      for (let i = left; i <= right; i += 1) {
        if (!visited[nRow + i] && matchesIndex(nRow + i)) stack.push(i, ny);
      }
    }
  }

  return result;
}

/** Select > Grow: contiguous expansion of an existing selection into similar colours. */
export function growCoverage(
  image: ColorSelectImage,
  coverage: Uint8Array,
  tolerance: number,
): Uint8Array {
  const { pixels, width, height } = image;
  const size = width * height;
  if (width <= 0 || height <= 0) return new Uint8Array(0);

  const result = Uint8Array.from(coverage);
  const inSelection = new Uint8Array(size);
  const stack: number[] = [];
  for (let i = 0; i < size; i += 1) {
    if (coverage[i] >= 128) {
      inSelection[i] = 1;
      stack.push(i);
    }
  }

  const limit = toLimit(tolerance);
  // Each step compares to the colour of the pixel it grows *from*, not the
  // original seed, so the flood can walk along a gradient one tolerance-sized
  // hop at a time and still stop dead at a genuinely hard edge.
  const tryGrow = (neighbor: number, fromOffset: number): void => {
    if (inSelection[neighbor]) return;
    if (pixelDiff(pixels, fromOffset, neighbor * 4) <= limit) {
      inSelection[neighbor] = 1;
      result[neighbor] = 255;
      stack.push(neighbor);
    }
  };

  while (stack.length > 0) {
    const index = stack.pop()!;
    const x = index % width;
    const y = (index - x) / width;
    const offset = index * 4;
    if (x > 0) tryGrow(index - 1, offset);
    if (x < width - 1) tryGrow(index + 1, offset);
    if (y > 0) tryGrow(index - width, offset);
    if (y < height - 1) tryGrow(index + width, offset);
  }

  return result;
}

const BUCKET_SHIFT = 3; // 256 >> 3 = 32 levels per RGB channel
const BUCKET_LEVELS = 1 << (8 - BUCKET_SHIFT);

function bucketKey(r: number, g: number, b: number, a: number): number {
  const qr = Math.min(BUCKET_LEVELS - 1, r >> BUCKET_SHIFT);
  const qg = Math.min(BUCKET_LEVELS - 1, g >> BUCKET_SHIFT);
  const qb = Math.min(BUCKET_LEVELS - 1, b >> BUCKET_SHIFT);
  const qa = a >= 128 ? 1 : 0;
  return ((qr * BUCKET_LEVELS + qg) * BUCKET_LEVELS + qb) * 2 + qa;
}

/** Select > Similar: every pixel in the document close to any selected colour. */
export function similarCoverage(
  image: ColorSelectImage,
  coverage: Uint8Array,
  tolerance: number,
): Uint8Array {
  const { pixels, width, height } = image;
  const size = width * height;
  if (width <= 0 || height <= 0) return new Uint8Array(0);

  const result = Uint8Array.from(coverage);

  /**
   * Selected colours are bucketed into a coarse 32x32x32x2 grid (RGB in steps of
   * 8, alpha split into opaque/transparent) rather than kept as a flat list, so
   * the number of reference colours checked per pixel is bounded by the grid
   * size instead of growing with how much of the document is already selected.
   * The cost is that distinct shades sharing a bucket collapse onto whichever
   * one was seen first, which can nudge the tolerance boundary for those shades.
   */
  const representatives = new Map<number, readonly [number, number, number, number]>();
  for (let i = 0; i < size; i += 1) {
    if (coverage[i] < 128) continue;
    const offset = i * 4;
    const key = bucketKey(pixels[offset], pixels[offset + 1], pixels[offset + 2], pixels[offset + 3]);
    if (!representatives.has(key)) {
      representatives.set(key, [
        pixels[offset],
        pixels[offset + 1],
        pixels[offset + 2],
        pixels[offset + 3],
      ]);
    }
  }
  if (representatives.size === 0) return result;

  const refs = Array.from(representatives.values());
  const limit = toLimit(tolerance);
  for (let i = 0; i < size; i += 1) {
    if (result[i] >= 128) continue;
    const offset = i * 4;
    for (const [r, g, b, a] of refs) {
      if (maxChannelDiff(pixels, offset, r, g, b, a) <= limit) {
        result[i] = 255;
        break;
      }
    }
  }
  return result;
}

function rampValue(distance: number, limit: number): number {
  if (distance <= 0) return 255;
  if (limit <= 0 || distance >= limit) return 0;
  return Math.round(255 * (1 - distance / limit));
}

/** Select > Color Range: soft coverage from one or more sampled colours. */
export function colorRangeCoverage(
  image: ColorSelectImage,
  samples: readonly RGBA[],
  options: { fuzziness: number; invert?: boolean },
): Uint8Array {
  const { pixels, width, height } = image;
  const size = width * height;
  if (width <= 0 || height <= 0) return new Uint8Array(0);
  if (samples.length === 0) return new Uint8Array(size);

  const limit = toLimit(options.fuzziness);
  const invert = options.invert ?? false;
  // Samples are 0..1 fractions (see core/types.ts RGBA); scale once up front so
  // the per-pixel loop stays in the same 0..255 domain as the pixel buffer.
  const sampleChannels = samples.map(
    (s) => [clamp01(s.r) * 255, clamp01(s.g) * 255, clamp01(s.b) * 255, clamp01(s.a) * 255] as const,
  );

  const result = new Uint8Array(size);
  for (let i = 0; i < size; i += 1) {
    const offset = i * 4;
    let nearest = Infinity;
    for (const [r, g, b, a] of sampleChannels) {
      const d = maxChannelDiff(pixels, offset, r, g, b, a);
      if (d < nearest) nearest = d;
    }
    result[i] = rampValue(nearest, limit);
  }

  if (!invert) return result;
  for (let i = 0; i < size; i += 1) result[i] = 255 - result[i];
  return result;
}
